import { noop, notUndefined } from '@taraai/utility/dist/functions';
import { CompositeDecorator, ContentState, EditorState, Modifier, SelectionState } from 'draft-js';
import { stateToMarkdown } from 'draft-js-export-markdown';
import { stateFromMarkdown } from 'draft-js-import-markdown';
import flow from 'lodash.flow';
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounced } from 'tools/utils/hooks/useDebounced';

/* eslint-disable @typescript-eslint/no-shadow */
import { getEntityRanges } from './entities';
import { composePlugins, createDefaultPlugin, useHandlers } from './plugins';
import { zwsp } from './plugins/utils';
import { ParseMode, RichEditorConfig, RichEditorHandlers } from './types';

type State = 'initialized' | 'modified' | 'saved';

export const RichEditorContext = createContext<{
  editorState: EditorState;
  forceSelectedEntity: string | undefined;
  handlers: RichEditorHandlers;
  onChange: (editorState: EditorState) => void;
  provided: boolean;
  readOnly: boolean;
  save: (content: ContentState) => void;
  setDisableHandlers: Dispatch<SetStateAction<boolean>>;
  setEditorState: Dispatch<SetStateAction<EditorState>>;
  setForceSelectedEntity: Dispatch<SetStateAction<string | undefined>>;
  setThreadIds: Dispatch<SetStateAction<string[]>>;
  showThread: (threadId: string) => void;
  singleLine: boolean;
  state: State;
  threadIds: string[];
  autoSaveWait?: number;
}>({
  editorState: EditorState.createEmpty(),
  forceSelectedEntity: undefined,
  handlers: {},
  onChange: noop,
  provided: false,
  readOnly: false,
  save: noop,
  setDisableHandlers: noop,
  setEditorState: noop,
  setForceSelectedEntity: noop,
  setThreadIds: noop,
  showThread: noop,
  singleLine: false,
  state: 'initialized',
  threadIds: [],
});

interface Props {
  children: ReactNode;
  config: RichEditorConfig;
  initialValue?: string;
  onSave?: (value: string) => void | Promise<void> | Promise<string | Error | null>;
  onTextChange?: (text: string) => void;
  readOnly?: boolean;
  selectAllContent?: boolean;
  saveStrategy?: 'saveOnReturn' | 'autoSave' | 'both';
  autoSaveWait?: number;
}

/**
 * The main goal of `RichEditorProvider` component is to
 * provide a single source of truth for the internal editor state
 * and to share it between `RichEditor` and `Toolbar` components.
 */
export function RichEditorProvider({
  children,
  config: { parseMode, plugin, singleLine = false },
  initialValue = '',
  onSave = noop,
  onTextChange,
  readOnly: forceReadOnly,
  selectAllContent = false,
  saveStrategy = 'autoSave',
  autoSaveWait = 1000,
}: Props): JSX.Element {
  const [state, setState] = useState<State>('initialized');
  const [disableHandlers, setDisableHandlers] = useState(false);
  const [forceSelectedEntity, setForceSelectedEntity] = useState<string>();
  const saveOnReturn = saveStrategy === 'saveOnReturn' || saveStrategy === 'both';
  const pluginWithDefaults = useMemo(
    () => composePlugins(...[plugin, createDefaultPlugin({ saveOnReturn, singleLine })].filter(notUndefined)),
    [plugin, saveOnReturn, singleLine],
  );

  const [editorState, setEditorState] = useState(() =>
    flow(
      pluginWithDefaults.preRead,
      (text) => parseText(parseMode, text),
      pluginWithDefaults.read,
      (content) => EditorState.createWithContent(content, new CompositeDecorator(pluginWithDefaults.decorator)),
      (state) => (selectAllContent ? selectAllEditorContent(state) : state),
    )(initialValue),
  );

  const save = useCallback(
    async (content: ContentState): Promise<string | Error | null | void> => {
      return flow(
        pluginWithDefaults.save,
        (content) => formatContent(parseMode, content),
        pluginWithDefaults.postSave,
        onSave,
      )(content);
    },
    [onSave, parseMode, pluginWithDefaults.postSave, pluginWithDefaults.save],
  );

  const [threadIds, setThreadIds] = useState<string[]>([]);

  // @TODO - show thread component when designs will be ready to implement that
  const showThread = (threadId: string): string => {
    return threadId;
  };

  const handlers = useHandlers(pluginWithDefaults, { save, setEditorState, setForceSelectedEntity, setThreadIds });

  const debouncedSave = useDebounced(save, autoSaveWait);
  const prevEditorStateRef = useRef(editorState);
  const autoSave = saveStrategy === 'autoSave' || saveStrategy === 'both';

  useEffect(() => {
    const prevEditorState = prevEditorStateRef.current;
    prevEditorStateRef.current = editorState;
    const prevContent = prevEditorState.getCurrentContent();
    const nextContent = editorState.getCurrentContent();

    if (nextContent.getPlainText() !== prevContent.getPlainText()) onTextChange?.(nextContent.getPlainText());

    if (nextContent !== prevContent && autoSave) debouncedSave(nextContent);
  }, [autoSave, debouncedSave, editorState, onTextChange]);

  const onChange = useCallback((nextState: EditorState) => {
    setState('modified');
    setEditorState(nextState);
  }, []);

  const readOnly =
    forceReadOnly ||
    // Draft.js source code says that if readOnly is on, all handlers are disabled
    disableHandlers;

  return (
    <RichEditorContext.Provider
      value={useMemo(
        () => ({
          editorState,
          forceSelectedEntity,
          handlers,
          onChange,
          provided: true,
          readOnly,
          save,
          setDisableHandlers,
          setEditorState,
          setForceSelectedEntity,
          setThreadIds,
          showThread,
          singleLine,
          state,
          threadIds,
        }),
        [editorState, forceSelectedEntity, handlers, onChange, readOnly, save, singleLine, state, threadIds],
      )}
    >
      {children}
    </RichEditorContext.Provider>
  );
}

export function useRichEditorContextGuard(): void {
  if (!useContext(RichEditorContext).provided) throw new Error('Tried to use RichEditor without RichEditorProvider');
}

export function parseText(parseMode: ParseMode, text: string): ContentState {
  return parseMode === 'plaintext'
    ? ContentState.createFromText(text)
    : replaceImageSpaceWithZwsp(stateFromMarkdown(text));
}

export function formatContent(parseMode: ParseMode, content: ContentState): string {
  return parseMode === 'plaintext' ? content.getPlainText() : stateToMarkdown(content);
}

/**
 * Our current markdown plugin is using a space character for images, which generates a lot of problems.
 * Unfortunately it's not possible to pass any option that would change that, so we need some post-processing.
 */
function replaceImageSpaceWithZwsp(content: ContentState): ContentState {
  return content.getBlocksAsArray().reduce(
    (content, block) =>
      getEntityRanges('IMAGE', content, block).reduce(
        (content, [start, end]) =>
          Modifier.replaceText(
            content,
            SelectionState.createEmpty(block.getKey()).merge({
              anchorOffset: start,
              focusOffset: end,
            }) as SelectionState,
            zwsp,
            block.getInlineStyleAt(start),
            block.getEntityAt(start),
          ),
        content,
      ),
    content,
  );
}

function selectAllEditorContent(state: EditorState): EditorState {
  const content = state.getCurrentContent();
  return EditorState.forceSelection(
    state,
    new SelectionState({
      anchorKey: content.getFirstBlock().getKey(),
      anchorOffset: 0,
      focusKey: content.getLastBlock().getKey(),
      focusOffset: content.getLastBlock().getLength(),
    }),
  );
}
